// @file Surface comments store
import { trackEvent } from '@@/bits/analytics'
import { CONFIRM_MODERATION_REJECT_COMMENT, SELF_HARM_COMMENT_ALERT } from '@@/bits/confirmation_dialog'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { stripHtml } from '@@/bits/html_parser'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { COMMENT_BODY_CHAR_LIMIT } from '@@/bits/numbers'
import { getVuexStore } from '@@/bits/pinia'
import { findLast } from '@@/bits/set'
import { ACCESS_WITHDRAWN } from '@@/bits/snackbar_helper'
import { addCommentCidIfAbsent, newCommentDraft } from '@@/bits/surface_comments'
import type { UploadJob } from '@@/bits/uploader'
import { selfHarmHelplineUrl } from '@@/bits/url'
import { CommentAttachmentState, CommentDraftState, SnackbarNotificationType } from '@@/enums'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceCommentAttachmentsStore } from '@@/pinia/surface_comment_attachments'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { CommentApi, CommentV2Api } from '@@/surface/api/comment'
import type { Cid, HashId, Id, Post, TranslatedComment, User } from '@@/types'
import type { Comment, CommentCid, CommentDraft, CommentId, DisplayedComment } from '@@/types/surface_comments'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import type { Comment as V1Comment, JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { cloneDeep, groupBy, isEqual, keyBy, mapValues, pickBy } from 'es-toolkit'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export interface CreateCommentPayload {
  postId: Id
  htmlBody?: string
  attachment?: string
}

export const useCommentsStore = defineStore('comments', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceCommentAttachments = useSurfaceCommentAttachmentsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()

  // #region Persisted comments

  /**
   * Persisted comments
   */

  const persistedCommentsById = ref<Record<CommentId, Comment>>({})

  const persistedComments = computed<Comment[]>(() => Object.values(persistedCommentsById.value))

  const addPersistedComment = (comment: Comment): void => {
    persistedCommentsById.value[comment.id] = addCommentCidIfAbsent(comment)
  }

  const setPersistedComments = (comments: Comment[]): void => {
    persistedCommentsById.value = comments.reduce((acc, comment) => {
      acc[comment.id] = addCommentCidIfAbsent(comment)
      return acc
    }, {})
  }

  const updatePersistedComment = (id: CommentId, changes: Partial<Comment>): void => {
    const existingComment = persistedCommentsById.value[id]
    if (existingComment == null) throw new Error(`Failed to update comment ${id}. Comment not found.`)
    persistedCommentsById.value[id] = { ...existingComment, ...changes, id }
  }

  const removePersistedComment = (commentId: CommentId): void => {
    delete persistedCommentsById.value[commentId]
  }

  // #endregion Persisted comments

  const isInitialCommentsLoadDone = ref<boolean>(false)
  const pendingCommentIds = ref<CommentId[]>([])
  const commentUnderActionId = ref<CommentId | null>(null)
  const activeNewCommentPostId = ref<Id | null>(null)
  const inlineCommentInputLastTypedInMs = ref<number>(0)
  const remoteComments = ref<Comment[]>([])

  const addPendingComment = (pendingComment: Comment): void => {
    addPersistedComment(pendingComment)
    pendingCommentIds.value.push(pendingComment.id)
  }

  const removePendingComment = ({ commentId }: { commentId: CommentId }): void => {
    const isCommentPending = pendingCommentIds.value.includes(commentId)
    if (!isCommentPending) return
    pendingCommentIds.value = pendingCommentIds.value.filter((id) => id !== commentId)
    removePersistedComment(commentId)
  }

  const user = computed<User>(() => surfaceVuexStore?.getters.user)

  const commentIds = computed<string[]>(() => {
    if (!isInitialCommentsLoadDone.value) return []
    return Object.keys(persistedCommentsById.value)
  })

  const hasComments = computed<boolean>(() => commentIds.value.length > 0)

  const commentsByPostId = computed<Record<Id, Comment[]>>(() => {
    if (!isInitialCommentsLoadDone.value) return {}
    return groupBy(Object.values(persistedCommentsById.value), (comment) => comment.wish_id)
  })

  const commentIdsByPost = computed<{ [wishId: number]: CommentId[] }>(() => {
    if (!isInitialCommentsLoadDone.value) return {}
    return mapValues(commentsByPostId.value, (comments) => comments.map((comment) => comment.id))
  })

  // If the CommentId array for a post does not change,
  // reuse the same array instead of creating a new one with the same contents.
  // Use this to prevent unnecessary re-renders.
  const stableCommentIdsByPostId = ref<Record<Id, CommentId[]>>({})
  watch(
    commentIdsByPost,
    (newValue) => {
      const currentStableIds = stableCommentIdsByPostId.value
      const result = {}
      Object.keys(newValue).forEach((key) => {
        const oldIds = currentStableIds[key]
        const newIds = newValue[key]
        result[key] = isEqual(oldIds, newIds) ? oldIds : newIds
      })
      stableCommentIdsByPostId.value = result
    },
    { immediate: true },
  )

  const isCommenting = computed<boolean>(() => hasCommentBeingEdited.value || activeNewCommentPostId.value != null)

  const unapprovedComments = computed<Comment[]>(() => {
    const comments: Comment[] = Object.values(commentEntities.value)
    return comments.filter((comment) => comment.is_awaiting_approval === true)
  })
  const isAddingNewComment = computed(() => activeNewCommentPostId.value != null)

  /**
   * A set of comment IDs that are currently being edited.
   */
  const commentsBeingEditedSet = ref<Set<CommentId>>(new Set())

  const hasCommentBeingEdited = computed(() => {
    return commentsBeingEditedSet.value.size > 0
  })

  const isCommentBeingEdited = (commentId: CommentId): boolean => {
    return commentsBeingEditedSet.value.has(commentId)
  }

  const latestCommentBeingEdited = computed(() => findLast(commentsBeingEditedSet.value))

  // #region Translation

  /**
   * Translation
   */

  const commentTranslationsById = computed<Record<CommentId, TranslatedComment>>(() => {
    if (!surfaceTranslationsStore.isViewingTranslatedPadlet) return {}
    if (surfaceTranslationsStore.translatedWall?.comments == null) return {}
    return keyBy(surfaceTranslationsStore.translatedWall.comments, (comment) => comment.commentId)
  })

  const translatedCommentBodiesById = computed<Record<CommentId, string>>(() => {
    return mapValues(commentTranslationsById.value, (comment) => comment.htmlBody)
  })

  const translatedPersistedComments = computed<Comment[]>(() => {
    if (isEmpty(translatedCommentBodiesById.value)) return persistedComments.value
    return persistedComments.value.map((comment) => {
      const translatedBody = translatedCommentBodiesById.value[comment.id]
      if (translatedBody == null) return comment
      return { ...comment, html_body: translatedBody, body: stripHtml(translatedBody) }
    })
  })

  const translatedPersistedCommentsById = computed<Record<CommentId, Comment>>(() => {
    if (isEmpty(translatedCommentBodiesById.value)) return persistedCommentsById.value
    return keyBy(translatedPersistedComments.value, (comment) => comment.id)
  })

  const commentEntities = translatedPersistedCommentsById

  // #region Translation

  // #region Drafts (New & Edited)

  /**
   * Drafts (New & Edited)
   */

  const commentDraftsByCid = ref<Record<Cid, CommentDraft>>({})

  const allCommentDrafts = computed<CommentDraft[]>(() => Object.values(commentDraftsByCid.value))

  const draftsForNewComments = computed<CommentDraft[]>(() => {
    return allCommentDrafts.value.filter((comment) => comment.id == null)
  })

  const draftsForNewCommentsBeingWritten = computed<CommentDraft[]>(() => {
    return draftsForNewComments.value.filter((comment) => comment.draftState === CommentDraftState.Writing)
  })

  const draftsForEditedComments = computed<CommentDraft[]>(() => {
    return allCommentDrafts.value.filter((comment) => comment.id != null)
  })

  const draftsForEditedCommentsById = computed<Record<CommentId, CommentDraft>>(() => {
    return keyBy(draftsForEditedComments.value, (draft) => draft.id!)
  })

  const addCommentDraft = (draftAttributes: Partial<CommentDraft>): void => {
    const draft = newCommentDraft(draftAttributes)
    commentDraftsByCid.value[draft.cid] = draft
  }

  const removeCommentDraft = (cid: CommentCid): void => {
    delete commentDraftsByCid.value[cid]
  }

  // #endregion Drafts (New & Edited)

  // #region Displayed Comments

  /**
   * DISPLAYED COMMENTS - Combines persisted & draft comments
   */

  /**
   * This combines the data in to what the user actually sees:
   * - Includes comments awaiting approval only if the user has access to them
   * - Translates the comments, if needed
   * - Merges in optimistic changes
   * - Includes metadata that's helpful for displaying the post
   * - Specifies ordering, if needed
   *
   * WIP!
   */
  const displayedComments = computed<DisplayedComment[]>(() => {
    if (!isInitialCommentsLoadDone.value) return []

    const canView = (comment) =>
      comment.is_awaiting_approval !== true ||
      comment.user_id === user.value?.id ||
      surfacePermissionsStore.canIModerate

    const viewableTranslatedPersistedComments = translatedPersistedComments.value.filter(canView)
    return viewableTranslatedPersistedComments
  })

  const displayedCommentsByPostId = computed<Record<Id, DisplayedComment[]>>(() => {
    return groupBy(displayedComments.value, (comment) => comment.wish_id)
  })

  const hasDisplayedComments = computed<boolean>(() => displayedComments.value.length > 0)

  /**
   * Returns the number of comments for each post including those pending approval.
   */
  const commentCountByPostId = computed<Record<Id, number>>(() => {
    return mapValues(commentsByPostId.value, (comments) => comments.length)
  })

  /**
   * Returns the number of approved comments for each post.
   */
  const publishedCommentCountByPostId = computed<Record<Id, number>>(() => {
    return mapValues(commentsByPostId.value, (comments) => {
      return comments.filter((comment) => comment.is_awaiting_approval !== true).length
    })
  })

  // #endregion Displayed Comments

  // #region NEW/DRAFT COMMENTS

  // Use this for backwards compatibility, for now.
  const newCommentByPostCid = computed<Record<Cid, CommentDraft>>(() => {
    return draftsForNewCommentsBeingWritten.value.reduce(
      (acc, draft) => {
        if (draft.wish_id == null) return acc
        const postCid = surfacePostsStore.postEntitiesById[draft.wish_id]?.cid
        if (postCid == null) return acc
        acc[postCid] = draft as CommentDraft
        return acc
      },
      {} as Record<Cid, CommentDraft>,
    )
  })

  const updateNewCommentByPostCid = (postCid: Cid, comment: Partial<CommentDraft>): void => {
    let draft = newCommentByPostCid.value[postCid]
    if (draft == null && isEmpty(comment)) return
    if (draft == null) {
      const postId = surfacePostsStore.postEntitiesByCid[postCid]?.id
      if (postId == null) return
      draft = newCommentDraft({ wish_id: postId })
    }
    addCommentDraft({ ...draft, ...comment })
  }

  const deleteNewCommentProperties = (postCid: Cid, props: Array<keyof CommentDraft>): void => {
    const draft = newCommentByPostCid.value[postCid]
    if (draft == null) return

    const newDraft = cloneDeep(draft)
    props.forEach((key) => {
      delete newDraft[key]
    })

    // If the object is now empty, delete it.
    const { cid, id: _id, draftState: _draftState, wish_id: _wish_id, ...content } = newDraft
    if (isEmpty(content)) {
      removeCommentDraft(cid)
    } else {
      addCommentDraft(newDraft)
    }
  }

  const updateNewCommentBody = ({ postCid, body }: { postCid: Cid; body: string }): void => {
    updateNewCommentByPostCid(postCid, { html_body: body })
  }

  const updateNewCommentAttachment = ({ postCid, attachment }: { postCid: Cid; attachment: string | null }): void => {
    if (attachment != null) {
      updateNewCommentByPostCid(postCid, { attachment })
    } else {
      deleteNewCommentProperties(postCid, ['attachment'])
    }
  }

  const updateNewCommentUploadingFile = ({
    postCid,
    file,
    uploadJob,
  }: {
    postCid: Cid
    file: File
    uploadJob: UploadJob
  }): void => {
    updateNewCommentByPostCid(postCid, { uploadingFile: file, uploadJob })
  }

  const updateNewCommentUploadingFileProgress = ({ postCid, progress }: { postCid: Cid; progress: number }): void => {
    if (newCommentByPostCid.value[postCid]?.uploadingFile == null) return
    updateNewCommentByPostCid(postCid, { uploadingProgress: progress })
  }

  const isUploadingAttachmentForNewComment = ({ postCid }: { postCid: Cid }): boolean =>
    newCommentByPostCid.value[postCid]?.uploadingFile != null

  /**
   * Takes action on this new comment.
   * - Cancels the upload job if there is one.
   * - Deletes all states related to uploading file.
   */
  const removeUploadingFileForNewComment = ({ postCid }: { postCid: Cid }): void => {
    newCommentByPostCid.value[postCid].uploadJob?.cancel()
    deleteNewCommentProperties(postCid, ['uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  /**
   * Removes all states related to attachment for this new comment.
   */
  const removeAttachmentForNewComment = ({ postCid }: { postCid: Cid }): void => {
    newCommentByPostCid.value[postCid]?.uploadJob?.cancel()
    deleteNewCommentProperties(postCid, ['attachment', 'uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  const newCommentBodyForPostCid = (postCid: Cid): string => {
    return newCommentByPostCid.value[postCid]?.html_body ?? ''
  }

  const newCommentAttachmentForPostCid = (postCid: Cid): string | null => {
    return newCommentByPostCid.value[postCid]?.attachment ?? null
  }

  const newCommentUploadingFileForPostCid = (postCid: Cid): File | null => {
    return newCommentByPostCid.value[postCid]?.uploadingFile ?? null
  }

  const newCommentUploadingProgressForPostCid = (postCid: Cid): number | null => {
    return newCommentByPostCid.value[postCid]?.uploadingProgress ?? null
  }

  const newCommentUploadJobForPostCid = (postCid: Cid): UploadJob | null => {
    return newCommentByPostCid.value[postCid]?.uploadJob ?? null
  }

  const resetComments = (): void => {
    commentDraftsByCid.value = {}
    commentsBeingEditedSet.value.clear()
  }
  // #endregion

  // #region COMMENTS BEING EDITED
  const editedCommentById = draftsForEditedCommentsById

  const updateEditedCommentById = (id: CommentId, comment: Partial<CommentDraft>): void => {
    let draft = draftsForEditedCommentsById.value[id]
    if (draft == null && isEmpty(comment)) return
    if (draft == null) {
      const postId = persistedCommentsById.value[id]?.wish_id
      if (postId == null) return
      draft = newCommentDraft({ id, wish_id: postId })
    }
    addCommentDraft({ ...draft, ...comment })
  }

  const deleteEditedCommentProperties = (commentId: CommentId, props: Array<keyof CommentDraft>): void => {
    const draft = draftsForEditedCommentsById.value[commentId]
    if (draft == null) return

    const newDraft = cloneDeep(draft)
    props.forEach((key) => {
      delete newDraft[key]
    })

    // If the object is now empty, delete it.
    const { cid, id: _id, draftState: _draftState, wish_id: _wish_id, ...content } = newDraft
    if (isEmpty(content)) {
      removeCommentDraft(cid)
    } else {
      addCommentDraft(newDraft)
    }
  }

  function updateEditedDraftCommentBody(payload: { commentId: CommentId; body: string }): void {
    updateEditedCommentById(payload.commentId, { html_body: payload.body })
  }

  const updateEditedDraftCommentAttachment = (payload: { commentId: CommentId; attachment: string | null }): void => {
    if (payload.attachment != null) {
      updateEditedCommentById(payload.commentId, { attachment: payload.attachment })
    } else {
      deleteEditedCommentProperties(payload.commentId, ['attachment'])
    }
  }

  const updateEditedDraftCommentUploadingFile = ({
    commentId,
    file,
    uploadJob,
  }: {
    commentId: CommentId
    file: File
    uploadJob: UploadJob
  }): void => {
    updateEditedCommentById(commentId, { uploadingFile: file, uploadJob })
  }

  const updateEditedDraftCommentUploadingFileProgress = ({
    commentId,
    progress,
  }: {
    commentId: CommentId
    progress: number
  }): void => {
    if (editedCommentById.value[commentId]?.uploadingFile == null) return
    updateEditedCommentById(commentId, { uploadingProgress: progress })
  }

  /**
   * Takes action on this comment.
   * - Cancels the upload job if there is one.
   * - Deletes all states related to uploading file.
   */
  const removeUploadingFileForEditedComment = ({ commentId }: { commentId: CommentId }): void => {
    editedCommentById.value[commentId].uploadJob?.cancel()
    deleteEditedCommentProperties(commentId, ['uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  /**
   * Removes all states related to attachment for this comment.
   */
  const removeAttachmentForEditedComment = ({ commentId }: { commentId: CommentId }): void => {
    editedCommentById.value[commentId].uploadJob?.cancel()
    deleteEditedCommentProperties(commentId, ['attachment', 'uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  const isUploadingAttachmentForEditedComment = ({ commentId }: { commentId: CommentId }): boolean =>
    editedCommentById.value[commentId]?.uploadingFile != null

  const editedDraftCommentBodyFromId = (commentId: CommentId): string => {
    return editedCommentById.value[commentId]?.html_body ?? ''
  }

  const editedDraftCommentAttachmentFromId = (commentId: CommentId): string | null => {
    return editedCommentById.value[commentId]?.attachment ?? null
  }

  const editedDraftCommentUploadingFileFromId = (commentId: CommentId): File | null => {
    return editedCommentById.value[commentId]?.uploadingFile ?? null
  }

  const editedDraftCommentUploadingProgressFromId = (commentId: CommentId): number | null => {
    return editedCommentById.value[commentId]?.uploadingProgress ?? null
  }

  const editedDraftCommentUploadJobFromId = (commentId: CommentId): UploadJob | null => {
    return editedCommentById.value[commentId]?.uploadJob ?? null
  }

  async function createCommentV2({ postId, htmlBody, attachment }: CreateCommentPayload): Promise<Comment | undefined> {
    const post: Post | undefined = surfaceVuexStore?.getters['post/getPostByServerId'](postId)
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    // tempId will be used to identify the comment in the frontend until it is persisted,
    // after which, we will use the server-provided Id.
    const tempId = `new_${Date.now()}`

    const newComment = {
      wish_id: postId,
      html_body: htmlBody,
      attachment,
      user_id: user.value.id,
      user_hashid: user.value.hashid,
    }

    addPendingComment({ id: tempId, ...newComment } as Comment)

    try {
      // Delete the new comment from the store and reset the attachment state to IDLE.
      // - The new comment isn't published, it's not new anymore.
      // - Reverse the state back to IDLE so that new comments can be created again.
      deleteNewCommentProperties(post.cid, ['html_body', 'attachment'])
      surfaceCommentAttachments.updateNewCommentAttachmentState({
        postCid: post.cid,
        state: CommentAttachmentState.IDLE,
      })

      const response = await CommentV2Api.create(newComment, {
        apiVersion: 8,
      })
      const comment = (response.data as JsonAPIResource<Comment>).attributes

      removePendingComment({ commentId: tempId })
      addPersistedComment(comment)

      if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
        void surfaceTranslationsStore.translateCommentEntity(comment.id)
      }
      return comment
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceCreateCommentV2' })
      if (post == null || post.cid == null) {
        removePendingComment({ commentId: tempId })
      } else {
        const comment = persistedCommentsById.value[tempId]
        removePendingComment({ commentId: tempId })
        updateNewCommentBody({ postCid: post.cid, body: comment.html_body ?? comment.body })
        updateNewCommentAttachment({ postCid: post.cid, attachment: comment.attachment ?? null })
        return comment
      }
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void useNativeAppStore().postSurfaceState()
    }
    return undefined
  }

  function startEditingCommentV2({ commentId }: { commentId: CommentId }): void {
    commentsBeingEditedSet.value.add(commentId)
    const editingComment = persistedCommentsById.value[commentId]
    updateEditedCommentById(commentId, {
      html_body: editingComment?.html_body,
      attachment: editingComment?.attachment,
    })
    if (editingComment?.attachment != null) {
      surfaceCommentAttachments.updateEditedCommentAttachmentState({ commentId, state: CommentAttachmentState.PREVIEW })
    }
  }

  async function editCommentV2({
    id,
    payload,
  }: {
    id: CommentId
    payload: { htmlBody?: string; attachment?: string | null }
  }): Promise<void> {
    // Store original values for potential reversion on error
    const originalComment = persistedCommentsById.value[id]

    // Comparing to existingComment so that only comments that already exceed character limit can continue to exceed character limit
    const existingCommentBody = originalComment?.html_body ?? ''
    if (
      existingCommentBody.length <= COMMENT_BODY_CHAR_LIMIT &&
      payload.htmlBody != null &&
      payload.htmlBody.length > COMMENT_BODY_CHAR_LIMIT
    ) {
      showCommentCharacterLimitSnackbar()
      return
    }

    try {
      // Optimistically update the comment in the store.
      // Adds the comment if it is missing from the store.
      addPersistedComment({ ...originalComment, html_body: payload.htmlBody, attachment: payload.attachment, id })

      const {
        data: {
          attributes: { html_body: newHtmlBody, body: newBody, attachment: newAttachment },
        },
      } = await CommentV2Api.update(id as number, payload, {
        apiVersion: 8,
      })

      updatePersistedComment(id, { html_body: newHtmlBody, body: newBody, attachment: newAttachment })

      const shouldTranslateComment = existingCommentBody !== payload.htmlBody
      if (shouldTranslateComment && surfaceTranslationsStore.isViewingTranslatedPadlet) {
        void surfaceTranslationsStore.translateCommentEntity(id)
      }
      stopEditingComment(id)
    } catch (e) {
      // Revert optimistic update but preserve user's draft changes
      if (originalComment != null) {
        updatePersistedComment(id, originalComment)
      } else {
        removePersistedComment(id)
      }
      // Keep the user's changes in the draft state (preserve their failed edits)
      if (payload.htmlBody !== undefined) {
        updateEditedCommentById(id, { html_body: payload.htmlBody })
      }
      if (payload.attachment !== undefined) {
        updateEditedCommentById(id, { attachment: payload.attachment })
      }
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceEditCommentV2' })
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void useNativeAppStore().postSurfaceState()
    }
  }

  function markCommentAsApproved(commentId: CommentId): void {
    updatePersistedComment(commentId, { is_awaiting_approval: false })
  }

  async function approveComment(comment: Comment): Promise<void> {
    await CommentV2Api.approve(Number(comment.id))
    markCommentAsApproved(comment.id)
  }

  async function rejectComment(comment: Comment): Promise<void> {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...CONFIRM_MODERATION_REJECT_COMMENT,
      afterConfirmActions: [
        async () => {
          removePersistedComment(comment.id)
          await CommentApi.delete(comment)
        },
      ],
    })
  }

  function showCommentCharacterLimitSnackbar(): void {
    void globalSnackbarStore.setSnackbar({
      message: __('Comment character limit reached'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function askToDeleteComment({ commentId }: { commentId: Id }): void {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      title: __('Delete this comment?'),
      body: __('Are you sure you want to delete this comment? This cannot be undone!'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Cancel'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [async () => await deleteComment({ commentId })],
    })
  }
  // #endregion

  function showSelfHarmCommentAlert({ comment_id }: { comment_id?: Id }): void {
    if (comment_id == null) return

    trackEvent('SurfaceSelfHarmWarning', 'Showed Self-harm dialog for comment', comment_id)
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...SELF_HARM_COMMENT_ALERT,
      afterConfirmActions: [() => navigateTo(selfHarmHelplineUrl(), { target: 'blank' })],
    })
  }

  /* ---------------------- */
  /* UNSAVED CHANGES        */
  /* ---------------------- */

  const isAnyCommentInputFocused = ref<boolean>(false)

  const hasUnsavedCommentDrafts = computed((): boolean => {
    // Check if any new comment drafts have content
    const hasNewCommentDrafts = Object.values(newCommentByPostCid.value).some(
      (draft) => draft?.html_body && draft.html_body.trim().length > 0,
    )

    // Check if any edited comment drafts have content
    const hasEditedCommentDrafts = Object.values(editedCommentById.value).some(
      (draft) => draft?.html_body && draft.html_body.trim().length > 0,
    )

    return hasNewCommentDrafts || hasEditedCommentDrafts
  })

  const shouldShowUnsavedChangesDialog = computed((): boolean => {
    return hasUnsavedCommentDrafts.value && isAnyCommentInputFocused.value
  })

  function setIsAnyCommentInputFocused(focused: boolean): void {
    isAnyCommentInputFocused.value = focused
  }

  // #region Fetch Comments
  /* ---------------------- */
  /* COMMENTS FETCHING      */
  /* ---------------------- */

  const postsWhereCommentsAreFetched = ref<Record<Id, boolean>>({})
  const hasDispatchedCommentFetchForPost = (postId: Id): boolean => postsWhereCommentsAreFetched.value[postId] ?? false

  // Calls the appropriate API to fetch comments based on app capabilities and settings
  async function callFetchAPI({
    wishId,
    wishHashid,
    pageStart,
  }: {
    wishId?: Id
    wishHashid?: HashId
    pageStart?: string
  }): Promise<JsonAPIResponse<Comment | V1Comment>> {
    if (!surfaceVuexStore?.state.shouldFallbackWsDataFetchToRest && isAppUsing('realtimeFetching')) {
      const params = pageStart ? { wishId, wishHashid, pageStart } : { wishId, wishHashid }
      return await surfaceVuexStore?.dispatch('realtime/fetchComments', params, { root: true })
    }

    const params = wishHashid != null ? { wishHashid } : { wallHashid: surfaceVuexStore?.getters.wallHashid }
    if (pageStart) {
      params['pageStart'] = pageStart
    }
    return await CommentV2Api.fetch(params)
  }

  // shouldResetState is a flag to reset the stored comment data
  // currently it is only set to true for the initial comment load when a Padlet page is first loaded
  async function fetchComments({
    wishId,
    wishHashid,
    shouldResetState,
  }: {
    wishId?: Id
    wishHashid?: HashId
    shouldResetState?: boolean
  }): Promise<void> {
    let fetchedData
    try {
      fetchedData = await callFetchAPI({ wishId, wishHashid })
    } catch (e) {
      // Don't capture exception when it's 401, show a snackbar instead
      if (e.status === 401) {
        useGlobalSnackbarStore().setSnackbar(ACCESS_WITHDRAWN)
      } else {
        captureFetchException(e, { source: 'fetchComments' })
      }
      return
    }
    const { data, meta } = fetchedData
    const nextPageCursor = meta?.next
    fetchCommentsSuccess({
      newComments: data,
      wishId,
      isFirstPage: true,
      isLastPage: nextPageCursor == null || nextPageCursor === '',
      shouldResetState,
    })

    if (nextPageCursor != null && nextPageCursor !== '') {
      await fetchCommentsForPage({ wishId, wishHashid, pageStart: nextPageCursor })
    }

    if (wishId != null) {
      postsWhereCommentsAreFetched.value[wishId] = true
    }
  }

  async function fetchCommentsForPage({ wishId, wishHashid, pageStart }): Promise<void> {
    let commentPage
    try {
      commentPage = await callFetchAPI({ wishId, wishHashid, pageStart })
    } catch (e) {
      captureFetchException(e, { source: 'fetchCommentsForPage' })
      return
    }
    const { data, meta } = commentPage
    const nextPageCursor = meta?.next
    fetchCommentsSuccess({
      newComments: data,
      wishId,
      isFirstPage: meta?.isFirstPage,
      isLastPage: nextPageCursor == null || nextPageCursor === '',
      shouldResetState: false,
    })
    if (nextPageCursor != null && nextPageCursor !== '') {
      await fetchCommentsForPage({ wishId, wishHashid, pageStart: nextPageCursor })
    }
  }

  // During auto refresh, we will fetch the same page again
  // some data may have been deleted from the page but they are not synced by realtime and still exist in the page
  // we need to identify and delete them
  function idsToDeleteInPage(
    isFirstPage: boolean,
    isLastPage: boolean,
    newData: JsonAPIResource<Comment>[],
    existingEntitiesById: Record<string, Comment>,
  ): (string | number)[] {
    // the array returned from the back end is guaranteed to be ordered by id (DESC)
    const newIdList = newData.map((datum) => parseInt(datum.attributes.id as string))

    // last page should cover the smallest post id
    const lookupRangeStart = isLastPage ? 0 : newIdList[newData.length - 1]

    // first page should cover the biggest post id
    const lookupRangeEnd = isFirstPage ? Infinity : newIdList[0]

    return Object.keys(existingEntitiesById).filter((idKey: string): boolean => {
      const id: number = parseInt(existingEntitiesById[idKey].id as string)
      if (id > lookupRangeEnd || id < lookupRangeStart) {
        return false
      }

      // Anything that's inside the page's range but not in newIdList
      // means they are deleted in the backend but the frontend does not know yet
      return !newIdList.includes(id)
    })
  }

  // Helper to get the correct comment state from backend's data and what's currently in the frontend
  function reconcileCommentsAfterFetch({
    newComments,
    isFirstPage,
    isLastPage,
    shouldResetState,
  }): Record<CommentId, Comment> {
    const isOnlyPage = isFirstPage && isLastPage
    const newCommentEntities = shouldResetState || isOnlyPage ? {} : { ...persistedCommentsById.value }

    // we are paginating the comment API by id so newComments from fetched data only contains 1 page
    // during auto refresh, if a comment id that's supposed to be in the current page exists in the current state but not in newComments means it has been deleted
    // => we have to remove it from newCommentEntities
    if (
      !shouldResetState && // we are only updating the state, i.e auto refresh or appending new page
      isInitialCommentsLoadDone.value && // Don't try to remove anything when the first page load is not done
      newComments.length
    ) {
      const idsToDelete = idsToDeleteInPage(isFirstPage, isLastPage, newComments, newCommentEntities)
      idsToDelete.forEach((id) => {
        delete newCommentEntities[id]
      })
    }

    newComments.forEach((commentData) => {
      const comment = commentData.attributes
      newCommentEntities[comment.id] = comment
    })

    return newCommentEntities
  }

  // Helper to get the correct comment state from backend's data and what's currently in the frontend
  function reconcileCommentsForPostAfterFetch({
    newComments,
    wishId,
    isFirstPage,
    isLastPage,
    shouldResetState,
  }): Record<CommentId, Comment> {
    const isOnlyPage = isFirstPage && isLastPage
    const newCommentEntities =
      shouldResetState || isOnlyPage
        ? {}
        : (pickBy(persistedCommentsById.value, (comment) => comment.wish_id === wishId) as Record<CommentId, Comment>)

    // we are paginating the comment API by id so newComments from fetched data only contains 1 page
    // during auto refresh, if a comment id that's supposed to be in the current page exists in the current state but not in newComments means it has been deleted
    // => we have to remove it from newCommentEntities
    if (
      !shouldResetState && // we are only updating the state, i.e auto refresh or appending new page
      newComments.length
    ) {
      const idsToDelete = idsToDeleteInPage(isFirstPage, isLastPage, newComments, newCommentEntities)
      idsToDelete.forEach((id) => {
        delete newCommentEntities[id]
      })
    }

    newComments.forEach((commentData) => {
      const comment = commentData.attributes
      newCommentEntities[comment.id] = comment
    })

    return newCommentEntities
  }

  function fetchCommentsSuccess({
    newComments,
    wishId,
    isFirstPage,
    isLastPage,
    shouldResetState,
  }: {
    newComments: JsonAPIResource<Comment>[]
    wishId?: Id
    isFirstPage: boolean
    isLastPage: boolean
    shouldResetState?: boolean
  }): void {
    const newCommentEntities =
      wishId != null
        ? reconcileCommentsForPostAfterFetch({
            newComments,
            wishId,
            isFirstPage,
            isLastPage,
            shouldResetState,
          })
        : reconcileCommentsAfterFetch({
            newComments,
            isFirstPage,
            isLastPage,
            shouldResetState,
          })

    if (wishId == null) {
      setPersistedComments(Object.values(newCommentEntities))
    } else {
      const commentsForOtherPosts = Object.values(persistedCommentsById.value).filter(
        (comment) => comment.wish_id !== wishId,
      )
      setPersistedComments([...commentsForOtherPosts, ...Object.values(newCommentEntities)])
    }

    if (isLastPage) {
      isInitialCommentsLoadDone.value = true
      pendingCommentIds.value = []
    }

    void useNativeAppStore().postSurfaceState()
  }

  function refreshComments(payload: JsonAPIResponse<Comment>): void {
    const { data, meta } = payload
    if (!data || !Array.isArray(data)) return
    fetchCommentsSuccess({
      newComments: data,
      isFirstPage: meta?.isFirstPage,
      isLastPage: meta?.next == null || meta?.next === '',
      shouldResetState: false,
    })
  }

  // #endregion Fetch Comments

  // #region Vuex Actions

  /* ---------------------- */
  /* VUEX ACTIONS           */
  /* ---------------------- */

  function showCommentActionMenu({ commentId }: { commentId: CommentId }): void {
    if (commentUnderActionId.value) {
      commentUnderActionId.value = null
    } else {
      commentUnderActionId.value = commentId
    }
  }

  function hideCommentActionMenu(): void {
    commentUnderActionId.value = null
  }

  function stopEditingComment(commentId: CommentId): void {
    if (isCommentBeingEdited(commentId)) {
      const draft = draftsForEditedCommentsById.value[commentId]
      if (draft != null) removeCommentDraft(draft.cid)
    }
    commentsBeingEditedSet.value.delete(commentId)
  }

  function startWritingNewComment({ postId }: { postId: Id }): void {
    activeNewCommentPostId.value = postId
  }

  function stopWritingNewComment({ postId }: { postId?: Id }): void {
    if (activeNewCommentPostId.value === postId) {
      activeNewCommentPostId.value = null
    }
  }

  function resetWritingNewComment(payload: { post: Post }): void {
    deleteNewCommentProperties(payload.post.cid, ['html_body', 'attachment'])
    stopWritingNewComment({ postId: payload.post.id })
    surfaceCommentAttachments.removeNewCommentAttachmentState({ postCid: payload.post.cid })
  }

  async function deleteComment({ commentId }: { commentId: CommentId }): Promise<void> {
    const existingComment = persistedCommentsById.value[commentId]
    stopEditingComment(commentId)
    if (!existingComment) return
    try {
      removePersistedComment(existingComment.id)
      await CommentApi.delete(existingComment)
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceDeleteComment' })
      // Re-add the comment back to the store since deletion failed
      addPersistedComment(existingComment)
    }
    void useNativeAppStore().postSurfaceState()
  }

  function newCommentRemote({ comment }: { comment: Comment }): void {
    addPersistedComment(comment)
    remoteComments.value = [...remoteComments.value, comment]
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateCommentEntity(comment.id)
    }
    void useNativeAppStore().postSurfaceState()
  }

  function editCommentRemote({ comment }: { comment: Comment }): void {
    const existingComment = persistedCommentsById.value[comment.id]

    if (existingComment == null) {
      // For auto-moderated walls, a newly created comment is only published by realtime to the author.
      // Once it is auto-moderated, edit_comment is published to non-authors so we need to create the comment now.
      newCommentRemote({ comment })
      return
    }

    // real time can be out of sync sometimes.
    // make sure we are not updating a new copy with a stale one
    if (existingComment.updated_at > comment.updated_at) return

    updatePersistedComment(comment.id, comment)
    // Replace remoteComments with the newer comment
    remoteComments.value = remoteComments.value.map((c) => (c.id === comment.id ? comment : c))

    if (surfaceTranslationsStore.isViewingTranslatedPadlet && existingComment?.html_body !== comment.html_body) {
      void surfaceTranslationsStore.translateCommentEntity(comment.id)
    }

    void useNativeAppStore().postSurfaceState()
  }

  function deleteCommentRemote({ comment }: { comment: Comment }): void {
    if (isCommentBeingEdited(comment.id)) stopEditingComment(comment.id)
    if (commentUnderActionId.value === comment.id) {
      commentUnderActionId.value = null
    }
    removePersistedComment(comment.id)
    void useNativeAppStore().postSurfaceState()

    remoteComments.value = remoteComments.value.filter((c) => c.id !== comment.id)
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.deleteCommentFromTranslatedWall(comment.id)
    }
  }

  // Update the timestamp where user last actively type in the inline comment input
  function updateInlineCommentInputLastTypedInMs(): void {
    inlineCommentInputLastTypedInMs.value = Date.now()
  }

  // #endregion Vuex Actions

  // #region Comments Panel
  /* ---------------------- */
  /* COMMENTS PANEL         */
  /* ---------------------- */
  const xCommentsPanel = computed<boolean>(() => surfaceVuexStore?.state.xCommentsPanel)
  const commentsPanelPostId = computed<Id>(() => surfaceVuexStore?.state.commentsPanelPostId)
  const hideCommentsPanel = (): void => {
    void surfaceVuexStore?.dispatch('hideCommentsPanel')
  }

  // #endregion Comments Panel

  return {
    // Persisted comments
    persistedComments,

    hasComments,
    commentIds,
    commentIdsByPost,
    commentCountByPostId,
    publishedCommentCountByPostId,
    stableCommentIdsByPostId,
    isCommenting,
    inlineCommentInputLastTypedInMs,
    commentEntities,
    commentUnderActionId,
    activeNewCommentPostId,
    xCommentsPanel,
    commentsPanelPostId,
    unapprovedComments,
    commentsBeingEditedSet,
    hasCommentBeingEdited,
    isCommentBeingEdited,
    remoteComments,
    hasDispatchedCommentFetchForPost,

    // All drafts
    draftsForEditedCommentsById,

    // Displayed comments
    displayedCommentsByPostId,
    hasDisplayedComments,

    // New/draft comments
    newCommentByPostCid,
    isAddingNewComment,
    updateNewCommentByPostCid,
    isUploadingAttachmentForNewComment,
    newCommentBodyForPostCid,
    newCommentAttachmentForPostCid,
    newCommentUploadingFileForPostCid,
    newCommentUploadingProgressForPostCid,
    resetComments,
    updateNewCommentBody,
    updateNewCommentAttachment,
    updateNewCommentUploadingFile,
    updateNewCommentUploadingFileProgress,
    removeUploadingFileForNewComment,
    removeAttachmentForNewComment,
    newCommentUploadJobForPostCid,
    // Comments being edited
    editedCommentById,
    latestCommentBeingEdited,
    isUploadingAttachmentForEditedComment,
    updateEditedCommentById,
    deleteEditedCommentProperties,
    updateEditedDraftCommentBody,
    updateEditedDraftCommentAttachment,
    updateEditedDraftCommentUploadingFile,
    updateEditedDraftCommentUploadingFileProgress,
    removeUploadingFileForEditedComment,
    removeAttachmentForEditedComment,
    editedDraftCommentBodyFromId,
    editedDraftCommentAttachmentFromId,
    editedDraftCommentUploadingFileFromId,
    editedDraftCommentUploadingProgressFromId,
    editedDraftCommentUploadJobFromId,
    showCommentCharacterLimitSnackbar,

    // Actions
    createCommentV2,
    startEditingCommentV2,
    editCommentV2,
    markCommentAsApproved,
    approveComment,
    rejectComment,
    askToDeleteComment,
    fetchComments,
    refreshComments,
    showCommentActionMenu,
    hideCommentActionMenu,
    stopEditingComment,
    startWritingNewComment,
    stopWritingNewComment,
    resetWritingNewComment,
    deleteComment,
    showSelfHarmCommentAlert,
    newCommentRemote,
    editCommentRemote,
    deleteCommentRemote,
    updateInlineCommentInputLastTypedInMs,
    hideCommentsPanel,
    deleteNewCommentProperties,

    // Unsaved changes detection
    hasUnsavedCommentDrafts,
    shouldShowUnsavedChangesDialog,
    setIsAnyCommentInputFocused,
  }
})
